import {
  get,
  includes,
  reduce,
  first,
  size,
  filter,
  chain,
  omit,
} from "lodash";
import {
  TAG_STATIC_ATTRIBUTE,
  TAG_ATTRIBUTE_TYPES,
  TAG_STATIC_ATTRIBUTE_BG_TYPE,
  OMIT_KEYS_ON_UPDATE,
} from "src/consts/tags";
import urlPaths, { url, PERSON_TAB } from "src/consts/urlPaths";
import { hasAdminOrPowerRole, hasAdminRole } from "src/services/auth";

import memoizeOne from "./memoizeOne";

export const SPECIALIST = "SPECIALIST";

/**
 * Gets the tag ID from a tag object, handling both Tag and EntityTag structures
 * @param {Object} tag - Tag or EntityTag object
 * @returns {string|null} - The tag ID or null if not found
 */
export const getTagId = (tag) => {
  if (!tag) {
    return null;
  }
  return tag.tagId || tag?.tag?.id || tag.id || null;
};

export const getTagLabel = (tag) => {
  if (!tag) {
    return null;
  }

  return (
    tag.label ||
    tag.displayValue ||
    tag.name ||
    tag?.tag?.label ||
    tag?.tag?.displayValue ||
    tag?.tag?.name ||
    getTagId(tag)
  );
};

export const getTagsOfType = (tags, type) => {
  return filter(tags, (tag) => tag.type === type);
};

export const getTagTypeName = (tagType, tagConfigTypes) => {
  return get(tagConfigTypes, [tagType, "name"], tagType);
};

export const getVisibleTagTypes = memoizeOne((tagConfig, component) => {
  const tagTypeIds = reduce(
    tagConfig,
    (prev, config, id) => {
      const { visibleIn } = config;

      if (visibleIn && includes(visibleIn, component)) {
        prev.push(id);
      }

      return prev;
    },
    []
  );

  return tagTypeIds;
});

export const isTagVisible = (tag, tagConfig, component) => {
  if (!tagConfig[tag.type]) {
    return false;
  }

  if (component) {
    const { visibleIn } = tagConfig[tag.type];

    if (visibleIn && includes(visibleIn, component)) {
      return true;
    }
  }

  return false;
};

export const isPersonEntityTag = (tagTypeConfig) => {
  const values = get(tagTypeConfig, "attributes.values");
  const attribute = first(values);
  return size(values) === 1 && attribute?.type === TAG_ATTRIBUTE_TYPES.PERSON;
};

export const onTagClick = (tag, history) => {
  if (tag && history) {
    const values = get(tag, "tagTypeConfig.attributes.values");
    const attribute = first(values);
    const hasOnlyPersonAttribute =
      size(values) === 1 && attribute?.type === TAG_ATTRIBUTE_TYPES.PERSON;
    const personUrl = get(tag, `attributes.[${attribute?.id}]`);
    const nextUrl =
      hasOnlyPersonAttribute && personUrl
        ? url(urlPaths.PERSON_DETAILS, {
            id: personUrl,
            tabType: PERSON_TAB.DETAILS,
          })
        : url(urlPaths.TAG_DETAILS, {
            id: getTagId(tag),
          });
    history.push(nextUrl);
  }
};

export const isTagTypeReadOnly = ({ tagType, tagConfig, user }) => {
  const readOnlyFromConfig = get(tagConfig, [tagType, "readOnly"], false);
  return readOnlyFromConfig || !hasAdminOrPowerRole(user);
};

// Config-only read-only check — ignores user role. Use for isExternallyManaged
// to distinguish "tag type marked readOnly in config" from "user lacks edit role".
export const isTagTypeConfigReadOnly = ({ tagType, tagConfig }) => {
  return get(tagConfig, [tagType, "readOnly"], false);
};

export const areAllPersonTagTypesConfigReadOnly = ({
  personTagTypes,
  tagConfig,
}) => {
  if (!personTagTypes?.length) {
    return false;
  }
  return (
    filter(personTagTypes, (tagType) =>
      isTagTypeConfigReadOnly({ tagType, tagConfig })
    ).length === personTagTypes.length
  );
};

export const isTagAssignmentAdminOnly = ({ tagType, tagConfig }) => {
  return !!get(tagConfig, [tagType, "disableEditingTagsForNonAdmin"], false);
};

export const canEditTagAssignment = ({ tagType, tagConfig, user }) => {
  if (!hasAdminOrPowerRole(user)) return false;
  const config = get(tagConfig, tagType);
  if (!config) return true;
  if (config.readOnly) return false;
  if (config.disableEditingTagsForNonAdmin && !hasAdminRole(user)) return false;
  return true;
};

export const getTagTypesWithShowingHistory = (tagTypesConfig) => {
  return chain(tagTypesConfig)
    .filter(({ showPreviousAppliedTags }) => showPreviousAppliedTags)
    .map("id")
    .value();
};

export const roleBasedTagTypeIds = (tagTypes, visibleIn) =>
  filter(
    tagTypes,
    (config) =>
      includes(config?.visibleIn, visibleIn) &&
      !config?.isReserved &&
      !config?.isGroupAssociation
  ).map((config) => config.id);

export const getUnsplashImageAttr = (tag) => {
  if (tag?.attributes?.static_attributes?.[TAG_STATIC_ATTRIBUTE_BG_TYPE]) {
    return [tag.attributes.static_attributes[TAG_STATIC_ATTRIBUTE_BG_TYPE]];
  }
  return [];
};

export const getTagBgImage = (tag) => {
  return tag?.attributes?.[TAG_STATIC_ATTRIBUTE]?.[TAG_STATIC_ATTRIBUTE_BG_TYPE]
    ?.value;
};

export const buildTagInputForBgImageUpdate = (tag, newBackground) => {
  return {
    ...omit(tag, OMIT_KEYS_ON_UPDATE),
    attributes: {
      ...tag.attributes,
      [TAG_STATIC_ATTRIBUTE]: {
        ...tag.attributes?.[TAG_STATIC_ATTRIBUTE],
        [TAG_STATIC_ATTRIBUTE_BG_TYPE]: newBackground,
      },
    },
  };
};
